// 资料正文（Markdown）。迁移来的 [[双链]]、![[附件]] 按链接清单换成真实地址；受保护的图片带着登录令牌去取。

import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router'
import ReactMarkdown, { type Components } from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { fetchProtectedFile, isProtectedFile } from '../../api/kb'
import { isImageAsset, knowledgeRoute, remarkLingzhiWikiLinks, resolveMarkdownTarget, type LingzhiLinkManifest } from '../../lib/lingzhiLinks'

/** 开头的 YAML 头（--- … ---）是给 Obsidian 用的，不显示。 */
export const stripFrontmatter = (text: string) => text.replace(/^﻿?---\r?\n[\s\S]*?\r?\n---\r?\n?/, '')

const safeHref = (href?: string) => (href && (/^https?:\/\//i.test(href) || href.startsWith('#')) ? href : undefined)

export function ProtectedImage({ src, alt, kbId }: { src: string; alt?: string; kbId?: string }) {
  const [url, setUrl] = useState<string | null>(null)
  const [failed, setFailed] = useState(false)
  useEffect(() => {
    const controller = new AbortController()
    let objectUrl: string | null = null
    fetchProtectedFile(src, kbId, controller.signal)
      .then((blob) => setUrl((objectUrl = URL.createObjectURL(blob))))
      .catch(() => !controller.signal.aborted && setFailed(true))
    return () => {
      controller.abort()
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
  }, [src, kbId])
  if (failed) return <span className="doc-missing">［图片没能载入{alt ? `：${alt}` : ''}］</span>
  return url ? <img src={url} alt={alt ?? ''} loading="lazy" /> : <span className="doc-img-wait" aria-label={alt ? `正在载入图片：${alt}` : '正在载入图片'} />
}

function AssetLink({ url, label, kbId }: { url: string; label: string; kbId?: string }) {
  const [error, setError] = useState(false)
  const download = async () => {
    try {
      const blob = await fetchProtectedFile(url, kbId)
      const href = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = href
      a.download = label || '附件'
      a.click()
      setTimeout(() => URL.revokeObjectURL(href), 1000)
    } catch {
      setError(true)
    }
  }
  return (
    <button type="button" className="doc-asset" onClick={download} title={error ? '附件下载失败，请稍后重试' : '下载附件'}>
      {label}
      {error ? '（下载失败）' : ''}
    </button>
  )
}

export function MarkdownDoc({ text, manifest = {}, kbId }: { text: string; manifest?: LingzhiLinkManifest; kbId?: string }) {
  const components: Components = useMemo(
    () => ({
      a: ({ href, children }) => {
        const { link } = resolveMarkdownTarget(manifest, href)
        if (link?.kind === 'knowledge') {
          const to = knowledgeRoute(link)
          return to ? <Link to={to}>{children}</Link> : <>{children}</>
        }
        if (link?.kind === 'asset') return <AssetLink url={link.url} label={String(children ?? '附件')} kbId={kbId} />
        if (href?.startsWith('lingzhi-link:')) return <span className="doc-unresolved">{children}</span>
        const safe = safeHref(href)
        return safe ? (
          <a href={safe} target={safe.startsWith('#') ? undefined : '_blank'} rel="noopener noreferrer">
            {children}
          </a>
        ) : (
          <>{children}</>
        )
      },
      img: ({ src, alt }) => {
        const raw = typeof src === 'string' ? src : ''
        const { link } = resolveMarkdownTarget(manifest, raw)
        if (link && isImageAsset(link)) return <ProtectedImage src={link.url} alt={alt} kbId={kbId} />
        if (link?.kind === 'asset') return <AssetLink url={link.url} label={alt || '附件'} kbId={kbId} />
        if (isProtectedFile(raw)) return <ProtectedImage src={raw} alt={alt} kbId={kbId} />
        if (/^https:\/\//i.test(raw)) return <img src={raw} alt={alt ?? ''} loading="lazy" referrerPolicy="no-referrer" />
        return <span className="doc-missing">［{alt || '图片'}］</span>
      },
    }),
    [manifest, kbId],
  )
  return (
    <div className="prose">
      <ReactMarkdown remarkPlugins={[remarkGfm, remarkLingzhiWikiLinks]} components={components} skipHtml urlTransform={(u) => u}>
        {stripFrontmatter(text)}
      </ReactMarkdown>
    </div>
  )
}
